import * as Api from '@ViewModels';
import * as React from 'react';
import { useEventLogging } from '../../../../models/Logging';
import { useCoachLabCache } from './labCache';
import {
	coachingRequest,
	DEBRIEF_REQUEST,
	drawLeadOutcome,
	GAME_PLAN_ASK,
	gamePlanHandoff,
	hasGamePlan,
	type CallMix,
	type CoachLabNoteKind,
	type ICoachLabPersona,
	type ICoachLabTurn,
	MAX_TURNS,
	mightBeAnAsk,
	noteText,
	parseStepCheck,
	parseWrittenPersona,
	personaCard,
	personaWriterRequest,
	realLeadCard,
	splitHangUp,
	STEP_EARNED_MARKER,
	stepJudgeRequest,
} from './labLogic';
import { useCoachLabTransport } from './useCoachLabTransport';

export type CoachLabCallee =
	| { kind: 'lead'; companyId: string; label: string }
	| { kind: 'persona'; persona: ICoachLabPersona };

export interface ICoachLabCallSetup {
	coachTemplateId: string;
	prospectTemplateId: string;
	writerTemplateId?: string;
	/** Set only with a Coach Lab prospect, which is the one that understands the step-earned marker. */
	stepJudgeTemplateId?: string;
	vertical: string;
	bank: ICoachLabPersona[];
	/** Which outcomes a written persona is drawn from; see `CallMix`. */
	mix: CallMix;
	callee: CoachLabCallee;
}

type CoachLabPhase = 'idle' | 'preparing' | 'yourTurn' | 'coaching' | 'prospectReplying' | 'ended' | 'failed';

interface ICoachLabCallState {
	phase: CoachLabPhase;
	progress: string;
	error?: string;
	turns: ICoachLabTurn[];
	gamePlan?: string;
	persona?: ICoachLabPersona | null;
	leadLabel: string;
	companyId?: string;
	hungUp: boolean;
	/** The prospect's own account of why the call ended, asked for once it is over. */
	debrief?: { status: 'asking' | 'done' | 'failed'; text?: string };
	mix: CallMix;
}

const initialState: ICoachLabCallState = {
	hungUp: false,
	leadLabel: '',
	mix: 'real',
	phase: 'idle',
	progress: '',
	turns: [],
};

const DEBRIEF_TIMEOUT_MS = 45000;

const PLAN_MIN_LENGTH = 300;

const FAILURE_MESSAGE = 'Something went wrong talking to the coach or the prospect.';
const INTERRUPTED_MESSAGE = 'The call was interrupted, for example by the page reloading.';

/** Runs one Coach Lab call: the coach and prospect conversations, the Game Plan hand-off, and each turn after it. */
export const useCoachLabCall = () => {
	const transport = useCoachLabTransport();
	const { logApiError } = useEventLogging('CoachLab');
	const { cachedPersona, cachedPlan, markCalled, savePersona, savePlan } = useCoachLabCache();
	const [state, setState] = React.useState<ICoachLabCallState>(initialState);
	const latest = React.useRef(state);
	latest.current = state;
	const session = React.useRef<{
		coachId: string;
		prospectId: string;
		leadContext?: string;
		leadContextSent: boolean;
		lastRep?: string;
		turnCount: number;
		stepJudgeTemplateId?: string;
		outcome: string;
		stepEarned: boolean;
		coachTemplateId: string;
	} | null>(null);
	// One controller per call: hanging up, starting another call or leaving the page aborts it, which ends its streams and
	// stops every continuation of that call before it writes state.
	const run = React.useRef(new AbortController());

	// Hanging up, starting another call or leaving on purpose; any other abort (a reload mid-call) is an interruption.
	const stoppedOnPurpose = React.useRef(new WeakSet<AbortSignal>());
	// The step that failed, so the manager can try it again instead of losing the call.
	const debrief = React.useRef<Promise<string | undefined> | null>(null);
	const retryStep = React.useRef<((signal: AbortSignal) => Promise<void>) | null>(null);

	React.useEffect(() => {
		// A remount (a dev reload, or StrictMode) finds the old run aborted; later steps need a live one.
		if (run.current.signal.aborted) run.current = new AbortController();
		return () => run.current.abort();
	}, []);

	const stop = () => {
		stoppedOnPurpose.current.add(run.current.signal);
		run.current.abort();
	};

	const liveSignal = () => {
		if (run.current.signal.aborted) run.current = new AbortController();
		return run.current.signal;
	};

	const patch = (next: Partial<ICoachLabCallState>) => setState(prev => ({ ...prev, ...next }));

	const updateLastTurn = (change: (turn: ICoachLabTurn) => ICoachLabTurn) =>
		setState(prev => ({ ...prev, turns: [...prev.turns.slice(0, -1), change(prev.turns[prev.turns.length - 1])] }));

	// A stopped call (hung up, replaced or left) throws out of its awaits; it must end without writing state.
	const fail = (error: unknown, signal: AbortSignal) => {
		if (stoppedOnPurpose.current.has(signal)) return;
		logApiError('Call-Error', error as Api.IOperationResultNoValue);
		patch({ error: signal.aborted ? INTERRUPTED_MESSAGE : FAILURE_MESSAGE, phase: 'failed' });
	};

	const coachTurn = async (heard: string, signal: AbortSignal) => {
		const current = session.current;
		if (!current) return;
		current.turnCount += 1;
		const turnNumber = current.turnCount;
		retryStep.current = async retrySignal => {
			current.turnCount -= 1;
			setState(prev => ({ ...prev, turns: prev.turns.slice(0, -1) }));
			await coachTurn(heard, retrySignal);
		};
		setState(prev => ({
			...prev,
			phase: 'coaching',
			turns: [...prev.turns, { notes: [], number: turnNumber, prospect: heard }],
		}));
		const request = coachingRequest({
			heard,
			lastRep: current.lastRep,
			leadContext: current.leadContextSent ? undefined : current.leadContext,
		});
		current.leadContextSent = true;
		const card = await transport.send(current.coachId, request, signal, soFar =>
			updateLastTurn(t => ({ ...t, card: soFar }))
		);
		updateLastTurn(t => ({ ...t, card: card || '(The coach sent no card this turn.)' }));
		patch({ phase: 'yourTurn' });
	};

	const loadGamePlan = async (coachId: string, leadKey: string, companyId: string | undefined, signal: AbortSignal) => {
		const cached = cachedPlan(leadKey);
		if (cached) {
			await transport.send(coachId, gamePlanHandoff(cached), signal);
			return cached;
		}
		if (!companyId) return undefined;
		patch({
			progress:
				'Getting the real Game Plan for this lead. The first time can take a few minutes; after that it is instant.',
		});
		// The server answers this ask with the lead's pre-cached plan when there is one.
		const opened = await transport.send(coachId, GAME_PLAN_ASK, signal);
		let plan = hasGamePlan(opened) ? opened : null;
		if (!plan) {
			plan = await transport.previewGamePlan(companyId, signal);
			if (plan) await transport.send(coachId, gamePlanHandoff(plan), signal);
		}
		if (plan && plan.length > PLAN_MIN_LENGTH) savePlan(leadKey, plan);
		return plan ?? undefined;
	};

	const leadPersona = async (
		setup: ICoachLabCallSetup,
		companyId: string,
		label: string,
		leadContext: string | undefined,
		gamePlan: string | undefined,
		signal: AbortSignal
	) => {
		// The same lead is a different person under each mix, so each mix keeps its own copy.
		const cacheKey = setup.mix === 'longer' ? `${companyId}:longer` : companyId;
		const cached = cachedPersona(cacheKey);
		if (cached || !setup.writerTemplateId) return cached;
		patch({ progress: 'Building the person who answers the phone...' });
		const { outcome, styleIndex } = await drawLeadOutcome(companyId, setup.bank.length, setup.mix);
		const writerId = await transport.createConversation(setup.writerTemplateId, signal);
		const raw = await transport.send(
			writerId,
			personaWriterRequest({ gamePlan, leadContext, outcome, style: setup.bank[styleIndex], vertical: setup.vertical }),
			signal
		);
		const persona = parseWrittenPersona(raw, { companyId, org: label, outcome });
		if (persona) savePersona(cacheKey, persona);
		return persona;
	};

	const start = async (setup: ICoachLabCallSetup) => {
		stop();
		run.current = new AbortController();
		const { signal } = run.current;
		session.current = null;
		retryStep.current = null;
		debrief.current = null;
		const { callee } = setup;
		if (callee.kind === 'lead') markCalled(callee.companyId);
		const label = callee.kind === 'lead' ? callee.label : callee.persona.org;
		setState({
			...initialState,
			leadLabel: label,
			mix: setup.mix,
			phase: 'preparing',
			progress: 'Looking up the lead...',
		});
		try {
			let companyId = callee.kind === 'lead' ? callee.companyId : callee.persona.companyId;
			if (callee.kind === 'persona' && !companyId && callee.persona.sourceCallId) {
				companyId =
					(await transport.companyForCall(callee.persona.sourceCallId, signal).catch((): undefined => undefined)) ??
					undefined;
			}
			const leadContext =
				callee.kind === 'persona' ? callee.persona.context : await transport.leadContext(callee.companyId, signal);

			const leadLabel = label || leadContext?.match(/Lead Name:\s*(.+)/)?.[1]?.trim() || companyId || 'lead';
			patch({ companyId, leadLabel, progress: 'Opening the coach and the prospect...' });
			const [coachId, prospectId] = await Promise.all([
				transport.createConversation(setup.coachTemplateId, signal, companyId),
				transport.createConversation(setup.prospectTemplateId, signal),
			]);
			// Asked now so saving the call at the end does not wait on it.
			void transport.buildId(setup.coachTemplateId);
			session.current = {
				coachId,
				coachTemplateId: setup.coachTemplateId,
				outcome: 'Qualified Questions',
				stepEarned: false,
				stepJudgeTemplateId: setup.stepJudgeTemplateId,
				leadContext,
				leadContextSent: false,
				prospectId,
				turnCount: 0,
			};

			const gamePlan = await loadGamePlan(
				coachId,
				callee.kind === 'persona' ? callee.persona.id : callee.companyId,
				companyId,
				signal
			);
			patch({ gamePlan });

			const persona =
				callee.kind === 'persona'
					? callee.persona
					: await leadPersona(setup, callee.companyId, leadLabel, leadContext, gamePlan, signal);
			if (persona && session.current) session.current.outcome = persona.realOutcome;
			patch({ persona, progress: 'The phone is ringing...' });

			await transport.send(prospectId, persona ? personaCard(persona) : realLeadCard(leadLabel, leadContext), signal);
			const heard =
				(await transport.send(prospectId, 'The phone rings and you pick it up. Answer it.', signal)) || 'Hello?';
			await coachTurn(heard, signal);
		} catch (error) {
			fail(error, signal);
		}
	};

	const say = async (text: string) => {
		const current = session.current;
		const spoken = text.trim();
		if (!current || !spoken) return;
		const signal = liveSignal();
		updateLastTurn(t => ({ ...t, rep: spoken }));
		current.lastRep = spoken;
		try {
			await sendLine(spoken, signal);
		} catch (error) {
			fail(error, signal);
		}
	};

	/**
	 * Whether the rep's line earned the prospect's one step past its card, decided by the Step Judge rather than the
	 * prospect itself: asked to judge its own ceiling, the prospect never gave the step. Only lines that could be an ask
	 * are judged, and only until the step is earned once.
	 */
	// The newest line goes to the judge on its own; the last turn may or may not hold it yet, depending on render timing.
	const withoutNewestLine = (turns: ICoachLabTurn[]) =>
		turns.map((t, i) => (i === turns.length - 1 ? { ...t, rep: undefined } : t));

	const checkStep = async (spoken: string, signal: AbortSignal) => {
		const current = session.current;
		if (!current?.stepJudgeTemplateId || current.stepEarned || !mightBeAnAsk(spoken)) return null;
		try {
			const judgeId = await transport.createConversation(current.stepJudgeTemplateId, signal);
			const verdict = parseStepCheck(
				await transport.send(
					judgeId,
					stepJudgeRequest(current.outcome, withoutNewestLine(latest.current.turns), spoken),
					signal
				)
			);
			if (!verdict?.ask) return null;
			const stepCheck = { earned: verdict.earned, reason: verdict.reason };
			updateLastTurn(t => ({ ...t, stepCheck }));
			if (stepCheck.earned) current.stepEarned = true;
			return stepCheck;
		} catch (error) {
			if (signal.aborted) throw error;
			logApiError('StepCheck-Error', error as Api.IOperationResultNoValue);
			return null;
		}
	};

	/** The manager's line to the prospect, then the coach's card on the prospect's answer. */
	const sendLine = async (spoken: string, signal: AbortSignal) => {
		const current = session.current;
		if (!current) return;
		retryStep.current = retrySignal => sendLine(spoken, retrySignal);
		patch({ error: undefined, phase: 'prospectReplying' });
		const stepCheck = await checkStep(spoken, signal);
		const message = stepCheck?.earned ? `${spoken}\n\n${STEP_EARNED_MARKER}` : spoken;
		const { ended, text: reply } = splitHangUp(await transport.send(current.prospectId, message, signal));
		if (ended || current.turnCount >= MAX_TURNS) {
			setState(prev => ({
				...prev,
				hungUp: ended,
				phase: 'ended',
				turns: reply ? [...prev.turns, { notes: [], number: prev.turns.length + 1, prospect: reply }] : prev.turns,
			}));
			askDebrief();
			return;
		}
		await coachTurn(reply || '...', signal);
	};

	/** Tries the step that failed again: resending the manager's line, or asking the coach for the card again. */
	const retry = async () => {
		const step = retryStep.current;
		if (!step) return;
		const signal = liveSignal();
		patch({ error: undefined });
		try {
			await step(signal);
		} catch (error) {
			fail(error, signal);
		}
	};

	const addNote = (kind: CoachLabNoteKind, text: string) => {
		if (!text.trim()) return;
		updateLastTurn(t => ({ ...t, notes: [...t.notes, noteText(kind, text)] }));
	};

	const removeNote = (turnNumber: number, index: number) =>
		setState(prev => ({
			...prev,
			turns: prev.turns.map(t =>
				t.number === turnNumber ? { ...t, notes: t.notes.filter((_, i) => i !== index) } : t
			),
		}));

	const hangUp = () => {
		stop();
		patch({ phase: 'ended' });
		askDebrief();
	};

	/**
	 * Asks the prospect, out of role, why it ended the call and what would have kept it on, so the manager can tell an
	 * ending the persona was set to from one their lines caused. Runs on its own timeout: the call's signal is already
	 * aborted by then.
	 */
	const askDebrief = () => {
		const current = session.current;
		if (!current || current.turnCount === 0) return;
		patch({ debrief: { status: 'asking' } });
		debrief.current = transport
			.send(current.prospectId, DEBRIEF_REQUEST, AbortSignal.timeout(DEBRIEF_TIMEOUT_MS))
			.then(answer => splitHangUp(answer).text || undefined)
			.catch((): undefined => undefined)
			.then(text => {
				patch({ debrief: text ? { status: 'done', text } : { status: 'failed' } });
				return text;
			});
	};

	/** The debrief for the saved session file, waiting for it if the manager saves before it arrives. */
	const debriefText = () => debrief.current ?? Promise.resolve(undefined);

	const buildId = () => (session.current ? transport.buildId(session.current.coachTemplateId) : Promise.resolve(''));

	const reset = () => {
		stop();
		retryStep.current = null;
		session.current = null;
		setState(initialState);
	};

	const canRetry = state.phase === 'failed' && !!retryStep.current;

	return { addNote, buildId, canRetry, debriefText, hangUp, removeNote, reset, retry, say, start, state };
};
